import Barcode from 'assets/svgs/barcode.svg';
import { Button } from 'components/Button';
import { DecoderText } from 'components/DecoderText';
import { Divider } from 'components/Divider';
import { Footer } from 'components/Footer';
import { Heading } from 'components/Heading';
import { HotkeyNav } from 'components/HotkeyNav';
import { Meta } from 'components/Meta';
import { Section } from 'components/Section';
import { Tag } from 'components/Tag';
import { Text } from 'components/Text';
import { usePageHotkeys, useHashNavigation } from 'hooks';
import { AnimatePresence, motion } from 'framer-motion';
import RouterLink from 'next/link';
import { useState, useEffect, useMemo } from 'react';
import { formatDate } from 'utils/date';
import { cssProps } from 'utils/style';
import styles from './Articles.module.css';

const categories = [
  { id: 'all', label: 'All', hotkey: 'A' },
  { id: 'software', label: 'Software', hotkey: 'S' },
  { id: 'book-reviews', label: 'Book Reviews', hotkey: 'B' },
  { id: 'reading', label: 'Reading', hotkey: 'R' },
  { id: 'workflows', label: 'Workflows', hotkey: 'W' },
  { id: 'other', label: 'Other', hotkey: 'O' },
];

const ArticlesPost = ({
  slug,
  title,
  abstract,
  date,
  featured,
  category,
  timecode,
  index,
}) => {
  const [dateTime, setDateTime] = useState(null);

  useEffect(() => {
    setDateTime(formatDate(date));
  }, [date]);

  return (
    <article
      className={styles.post}
      style={index !== undefined ? cssProps({ delay: index * 100 + 200 }) : undefined}
    >
      <RouterLink
        href={`/articles/${slug}`}
        scroll={false}
        className={styles.postLink}
      >
        <div className={styles.postDetails}>
          <div className={styles.postMeta}>
            <div aria-hidden className={styles.postDate}>
              <Divider notchWidth="64px" notchHeight="8px" />
              {dateTime}
            </div>
            <div className={styles.postTags}>
              {featured && <Tag className={styles.featuredTag}>Featured</Tag>}
              {category && <Tag>{category}</Tag>}
            </div>
          </div>
          <Heading as="h2" level={4}>
            {title}
          </Heading>
          <Text size="s" as="p">
            {abstract}
          </Text>
          <div className={styles.postFooter}>
            <Button secondary iconHoverShift icon="chevronRight" as="div">
              Read article
            </Button>
            <Text className={styles.timecode} size="s">
              {timecode}
            </Text>
          </div>
        </div>
      </RouterLink>
    </article>
  );
};


function SearchInput({ value, onChange }) {
  return (
    <div className={styles.searchWrapper}>
      <input
        type="text"
        className={styles.searchInput}
        placeholder="Search articles..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export const Articles = ({ posts, featured }) => {
  const validIds = useMemo(() => categories.map(c => c.id), []);
  const [activeCategory, setActiveCategory] = useHashNavigation(validIds, 'all', '/articles');
  const [searchQuery, setSearchQuery] = useState('');

  // Combine all posts with featured at the beginning
  const allPosts = useMemo(() => (featured ? [featured, ...posts] : posts), [featured, posts]);
  const hasPosts = allPosts.length > 0;

  // Handle page-level hotkeys for category navigation
  usePageHotkeys(categories, setActiveCategory, { enabled: hasPosts });

  // Filter posts by category and search
  const filteredPosts = useMemo(() => {
    let result = allPosts;

    // Filter by category
    if (activeCategory !== 'all') {
      result = result.filter(post => post.category === activeCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(post => {
        const titleMatch = post.title?.toLowerCase().includes(query);
        const abstractMatch = post.abstract?.toLowerCase().includes(query);
        const tagsMatch = post.tags?.some(tag => tag.toLowerCase().includes(query));
        return titleMatch || abstractMatch || tagsMatch;
      });
    }

    return result;
  }, [allPosts, activeCategory, searchQuery]);

  // Separate featured (pinned) from regular posts
  const pinnedPosts = filteredPosts.filter(post => post.featured);
  const regularPosts = filteredPosts.filter(post => !post.featured);

  return (
    <article className={styles.articles}>
      <Meta
        title="Articles"
        description="A collection of technical design and development articles. May contain incoherent ramblings."
      />
      <Section className={styles.content}>
        <header className={styles.header}>
          <Heading className={styles.heading} level={5} as="h1">
            <DecoderText text="Latest articles" />
          </Heading>
          <Barcode />
        </header>
        {/* Filters over an empty list only advertise the emptiness. */}
        {hasPosts && (
          <>
            <HotkeyNav
              items={categories}
              activeId={activeCategory}
              onSelect={setActiveCategory}
              className={styles.categoryNav}
            />
            <SearchInput value={searchQuery} onChange={setSearchQuery} />
          </>
        )}
        <AnimatePresence mode="wait">
          <motion.div
            key={`${activeCategory}-${searchQuery}`}
            className={styles.grid}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {filteredPosts.length > 0 ? (
              <>
                {pinnedPosts.map(({ slug, ...post }, index) => (
                  <ArticlesPost key={slug} slug={slug} index={index} {...post} />
                ))}
                {regularPosts.map(({ slug, ...post }, index) => (
                  <ArticlesPost
                    key={slug}
                    slug={slug}
                    index={index + pinnedPosts.length}
                    {...post}
                  />
                ))}
              </>
            ) : (
              <Text className={styles.noResults} size="l" as="p">
                {hasPosts ? 'No articles found.' : 'Nothing published yet.'}
              </Text>
            )}
          </motion.div>
        </AnimatePresence>
      </Section>
      <Footer />
    </article>
  );
};
